// Custom hook for report state management
import { useState, useEffect } from 'react';
import { DEFAULT_REPORT_SECTIONS, REPORT_TABS, REPORT_PRESETS, LOCKED_SECTIONS } from '../utils/reportConstants.js';
import { reportUtils } from '../utils/reportUtils.js';

export const useReportData = (defaultReportType = '') => {
  // Basic report data - initialize with defaultReportType if provided
  const [activeReportType, setActiveReportType] = useState(defaultReportType); // Use defaultReportType or empty to show selection page
  const [reportData, setReportData] = useState({
    title: '',
    description: ''
  });
  
  // Report sections with default structure
  const [reportSections, setReportSections] = useState(DEFAULT_REPORT_SECTIONS);
  
  // Date and site selection
  const [selectedPeriod, setSelectedPeriod] = useState({
    startDate: '',
    endDate: ''
  });
  const [selectedSite, setSelectedSite] = useState('');
  
  // External data
  const [sites, setSites] = useState([]);
  const [organizationSettings, setOrganizationSettings] = useState({
    companyName: 'Loading...'
  });
  
  // UI State
  const [activeTab, setActiveTab] = useState('cover-page');
  
  // Report state management
  const [currentReportId, setCurrentReportId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savedReportState, setSavedReportState] = useState(null);
  const [generatedReports, setGeneratedReports] = useState([]);

  // Auto-populate dates on component mount
  useEffect(() => {
    const startDate = reportUtils.getFirstWorkingDayOfMonth();
    const endDate = reportUtils.getTodayDate();
    
    setSelectedPeriod({
      startDate,
      endDate
    });
  }, []);

  // Load reports from localStorage on component mount
  useEffect(() => {
    try {
      const savedReports = localStorage.getItem('siteorbit_generated_reports');
      if (savedReports) {
        setGeneratedReports(JSON.parse(savedReports));
      }
    } catch (error) {
      console.error('Error loading saved reports:', error);
    }
  }, []);

  // Track changes whenever any relevant state changes
  useEffect(() => {
    const getCurrentState = () => reportUtils.getCurrentReportState(
      reportData, 
      reportSections, 
      selectedPeriod, 
      selectedSite, 
      [], // selectedPhotos - will be handled by photo hook
      {} // photoLabels - will be handled by photo hook
    );
    
    const hasChanges = reportUtils.trackChanges(savedReportState, getCurrentState);
    setHasUnsavedChanges(hasChanges);
  }, [reportData, reportSections, selectedPeriod, selectedSite, savedReportState]);

  // Helper function to update report sections
  const updateReportSection = (section, field, value) => {
    setReportSections(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Section include/exclude (stored inside reportSections.reportOptions so it saves with the report)
  const excludedSections = reportSections?.reportOptions?.excludedSections || [];
  const setExcludedSections = (updater) => {
    setReportSections(prev => {
      const current = prev?.reportOptions?.excludedSections || [];
      const next = (typeof updater === 'function' ? updater(current) : updater).filter(id => !LOCKED_SECTIONS.includes(id));
      return { ...prev, reportOptions: { ...(prev?.reportOptions || {}), excludedSections: next } };
    });
  };
  const isSectionIncluded = (tabId) => !excludedSections.includes(tabId);
  const toggleSectionIncluded = (tabId) => setExcludedSections(current =>
    current.includes(tabId) ? current.filter(id => id !== tabId) : [...current, tabId]
  );
  const presetExclusions = (preset) => preset.included ? REPORT_TABS.map(t => t.id).filter(id => !preset.included.includes(id)) : [];
  const applySectionPreset = (presetId) => {
    const preset = REPORT_PRESETS.find(p => p.id === presetId);
    if (preset) setExcludedSections(presetExclusions(preset));
  };
  const activeSectionPreset = (REPORT_PRESETS.find(p => {
    const expected = presetExclusions(p);
    return expected.length === excludedSections.length && expected.every(id => excludedSections.includes(id));
  }) || { id: 'custom' }).id;
  const includedSectionCount = REPORT_TABS.filter(t => isSectionIncluded(t.id)).length;

  // Get current report state for saving/comparison
  const getCurrentReportState = (selectedPhotos = [], photoLabels = {}, importedPhotos = []) => {
    return reportUtils.getCurrentReportState(
      reportData,
      reportSections,
      selectedPeriod,
      selectedSite,
      selectedPhotos,
      photoLabels,
      importedPhotos
    );
  };

  // Save report function - saves to both backend API and localStorage
  const saveReport = async () => {
    try {
      const reportState = getCurrentReportState();
      const reportId = currentReportId || reportUtils.generateReportId();
      
      // Validate required fields before saving
      if (!selectedSite) {
        console.error('❌ Cannot save report: No site selected');
        throw new Error('Please select a site before saving the report');
      }
      
      if (!selectedPeriod.startDate || !selectedPeriod.endDate) {
        console.error('❌ Cannot save report: Missing date range');
        throw new Error('Please select a date range before saving the report');
      }
      
      // Get site name for display
      const siteName = sites.find(site => site.id === selectedSite)?.site_name || selectedSite;
      
      // Prepare comprehensive report data for backend API - structure matches SavedReportData model
      const savedReportData = {
        title: reportData.title || `Progress Report - ${selectedPeriod.startDate}`,
        site: selectedSite,
        reportData: {
          // Fields directly matching SavedReportData Pydantic model
          title: reportData.title || `Progress Report - ${selectedPeriod.startDate}`,
          description: reportData.description || '',
          selectedSite: selectedSite,
          selectedPeriod: selectedPeriod,
          reportSections: reportState.reportSections,
          importedPhotos: reportState.importedPhotos || [],
          photoLabels: reportState.photoLabels || {},
          comprehensiveState: {
            // Additional state for recovery
            author: reportState.reportData.author,
            signature: reportState.reportData.signature,
            selectedPhotos: reportState.selectedPhotos || [],
            savedAt: new Date().toISOString()
          }
        }
      };
      
      console.log('📦 Saving comprehensive report with all sections and nested data...');
      console.log('   • Cover Page data');
      console.log('   • 14 sections (2.0-15.0) with all fields');
      console.log('   • User textboxes and photos from 11 sections');
      console.log('   • 5 dynamic resource tables from Section 9.0');
      console.log('   • Photo grids from Section 12.0');
      console.log('   • 4 technical drawing tables + custom tables from Section 13.0');
      console.log('   • All metrics and calculated fields');
      console.log('   • Photo management data (imported photos & labels)');
      console.log('📋 Report Data to Save:', {
        title: savedReportData.title,
        site: savedReportData.site,
        hasSections: !!savedReportData.reportData.reportSections,
        sectionCount: Object.keys(savedReportData.reportData.reportSections || {}).length
      });
      
      // Save to backend API
      const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      let backendReportId = reportId;
      
      // Check if report already exists
      if (currentReportId) {
        // Update existing report
        console.log('🔄 Updating existing report:', currentReportId);
        const requestBody = {
          title: savedReportData.title,
          site: savedReportData.site,
          reportData: savedReportData.reportData,
          status: 'Draft'
        };
        console.log('📤 PUT Request Body:', JSON.stringify(requestBody).substring(0, 500) + '...');
        
        const response = await fetch(`${API}/api/admin/saved-reports/${currentReportId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
          let errorMessage = `Server returned ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
          } catch (e) {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          }
          console.error('❌ Failed to update report in backend:', response.status, errorMessage);
          throw new Error(`Failed to update report (${response.status}): ${errorMessage}`);
        }
        
        const result = await response.json();
        backendReportId = result.id;
        console.log('✅ Report updated successfully in backend database');
        
      } else {
        // Create new report
        console.log('✨ Creating new report');
        console.log('📤 POST Request Body Preview:', {
          title: savedReportData.title,
          site: savedReportData.site,
          reportDataKeys: Object.keys(savedReportData.reportData),
          reportSectionsKeys: Object.keys(savedReportData.reportData.reportSections || {})
        });
        
        const response = await fetch(`${API}/api/admin/saved-reports`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(savedReportData)
        });
        
        if (!response.ok) {
          let errorMessage = `Server returned ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
          } catch (e) {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          }
          console.error('❌ Failed to save report to backend:', response.status, errorMessage);
          throw new Error(`Failed to save report (${response.status}): ${errorMessage}`);
        }
        
        const result = await response.json();
        backendReportId = result.id;
        console.log('✅ Report saved successfully to backend database');
      }
      
      // Update state only after successful backend save
      setCurrentReportId(backendReportId);
      setSavedReportState(reportState);
      setHasUnsavedChanges(false);
      
      console.log('✅ Report saved successfully (ID:', backendReportId, ')');
      console.log('📊 Report contains', Object.keys(reportState.reportSections).length, 'sections with comprehensive data');
      
      return backendReportId;
      
    } catch (error) {
      console.error('❌ Error saving report:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      // Re-throw with better error message
      throw new Error(error.message || 'Failed to save report. Please check your connection and try again.');
    }
  };

  return {
    // Report Data State
    activeReportType,
    setActiveReportType,
    reportData,
    setReportData,
    reportSections,
    setReportSections,
    updateReportSection,
    isSectionIncluded,
    toggleSectionIncluded,
    applySectionPreset,
    activeSectionPreset,
    includedSectionCount,
    
    // Selection State
    selectedPeriod,
    setSelectedPeriod,
    selectedSite,
    setSelectedSite,
    
    // External Data
    sites,
    setSites,
    organizationSettings,
    setOrganizationSettings,
    
    // UI State
    activeTab,
    setActiveTab,
    
    // Report Management State
    currentReportId,
    setCurrentReportId,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    savedReportState,
    setSavedReportState,
    generatedReports,
    setGeneratedReports,
    
    // Functions
    getCurrentReportState,
    saveReport
  };
};